import React, { FC, useMemo, useState } from 'react';
import { Box, LinearProgress, linearProgressClasses, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { createPortal } from 'react-dom';
import './HorizontalBarChart.styles.scss';

interface Props {
  value: number;
  label?: string;
  extraLabel?: string;
  color?: string;
  variant?: 'light' | 'dark';
  className?: string;
}

export const HorizontalBarChart: FC<Props> = ({ label, value, color, variant, extraLabel, className = '' }) => {
  const theme = useTheme();
  const [element, setElement] = useState<HTMLElement | undefined>();

  const textColor = useMemo(() => {
    if (theme.palette.mode === 'dark') {
      return theme.palette.text.primary;
    }

    return variant === 'light' ? theme.palette.primary.main : '#fff';
  }, [variant, theme]);

  const BorderLinearProgress = useMemo(
    () =>
      styled(LinearProgress)(() => ({
        height: 24,
        borderRadius: 0,
        backgroundColor: 'transparent',
        [`& .${linearProgressClasses.barColorPrimary}`]: {
          backgroundColor: color,
        },
      })),
    [color],
  );

  const labelLength = label?.length || 0;
  const formattedValue = value < 0 || (value > 0 && value < labelLength) ? labelLength + 1 : Math.max(value, 0.5);

  return (
    <div className={`position-relative mt-3 ${className}`}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%' }}>
          <BorderLinearProgress
            ref={(el: HTMLElement) => setElement(el)}
            variant="determinate"
            value={Math.abs(formattedValue)}
          />
        </Box>
        {!!extraLabel && !!element?.firstElementChild && (
          <ExtraLabel el={element.firstElementChild} value={value} extraLabel={extraLabel} textColor={textColor} />
        )}
      </Box>
      {label && (
        <Box className="very-small horizontal-bar-chart-label" style={{ color: textColor }}>
          {label}
        </Box>
      )}
    </div>
  );
};

const ExtraLabel: FC<{
  el: Element;
  value: number;
  extraLabel: string;
  textColor: string;
}> = ({ el, value, extraLabel, textColor }) => {
  const isOverflowing = value + 15 >= 100;
  const width = isOverflowing ? undefined : '100%';
  const left = isOverflowing ? undefined : 'calc(100% + 8px)';
  const right = isOverflowing ? '10px' : undefined;
  const color = isOverflowing ? textColor : 'inherit';

  return (
    <>
      {createPortal(
        <div className="position-relative w-100 h-100">
          <Box className="horizontal-bar-chart-extra-label" style={{ left, right, color, width }}>
            <p className="m-0 very-small fw-500 w-100">{extraLabel}</p>
          </Box>
        </div>,
        el,
      )}
    </>
  );
};
